/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { useContext } from 'react'
import { useIntl } from 'react-intl'
import ButtonGroup from '../../../../client/js/components/form/ButtonGroup'
import FormField from '../../../../client/js/components/form/fields/FormField'
import FormSaveButton from '../../../../client/js/components/form/buttons/FormSaveButton'
import {
  createFieldInitializer,
  createFieldValidator,
  createFormValidator,
  removeUnknownFields
} from '../../../../client/js/lib/form'
import { AuthContext } from '../../../auth/components/AuthProvider'
import EmailField from '../../../user/components/fields/EmailField'
import { putSettings } from '../../lib/rest/settingAPIClient'
import CompanySettingsSchema from '../../schemas/CompanySettingsSchema.mjs'
import PhoneField from '../fields/PhoneField'
import FormSection from '../FormSection'
import FormResetButton from '../../../../client/js/components/form/buttons/FormResetButton'
import { SimpleGrid } from '@mantine/core'
import FormNumberField from '../../../../client/js/components/form/fields/FormNumberField'
import SmartForm from '../../../../client/js/components/form/SmartForm'
import FormTextareaField from '../../../../client/js/components/form/fields/FormTextareaField'
import useCustomForm from '../../../../client/js/hooks/useCustomForm'

const initializeField = createFieldInitializer(CompanySettingsSchema)
const validateField = createFieldValidator(CompanySettingsSchema)
const validate = createFormValidator(CompanySettingsSchema)

export type CompanySettingsFormProps = {
  initialValues: Partial<Settings>
}

function CompanySettingsForm (props: CompanySettingsFormProps) {
  const { initialValues } = props
  const intl = useIntl()
  const auth = useContext(AuthContext)
  const editable = auth.hasPermission('write:setting')

  const form = useCustomForm({
    disabled: !editable,
    initialValues,
    initializeField,
    validateField,
    validate,
    onSubmit: (settings) => putSettings(removeUnknownFields(
      settings,
      CompanySettingsSchema
    ))
  })

  return (
    <SmartForm context={form}>
      <SimpleGrid cols={{ lg: 2 }}>
        <FormSection
          defaultOpened
          title={intl.formatMessage({
            id: 'legal_info',
            defaultMessage: 'Informations légales'
          })}
        >
          <FormField
            label={intl.formatMessage({
              id: 'company_name',
              defaultMessage: 'Nom de la société'
            })}
            name="companyName"
          />
          <FormTextareaField
            label={intl.formatMessage({
              id: 'head_office',
              defaultMessage: 'Siège de la société'
            })}
            name="companyAddress"
          />
          <FormField
            label={intl.formatMessage({
              id: 'legal_form',
              defaultMessage: 'Forme juridique'
            })}
            name="companyLegalForm"
          />
          <FormNumberField
            label={intl.formatMessage({
              id: 'share_capital',
              defaultMessage: 'Capital social'
            })}
            name="companyShareCapital"
            parser={String}
          />
          <FormField
            label={intl.formatMessage({
              id: 'rcs_number',
              defaultMessage: 'Numéro R.C.S.'
            })}
            name="companyRcsNumber"
          />
          <FormField
            label={intl.formatMessage({
              id: 'tahiti_number',
              defaultMessage: 'Numéro T.A.H.I.T.I.'
            })}
            name="companyTahitiNumber"
          />
          <FormField
            label={intl.formatMessage({
              id: 'financial_guarantee_fund',
              defaultMessage: 'Caisse de garantie financière'
            })}
            name="companyFinancialGuaranteeFund"
          />
          <FormField
            label={intl.formatMessage({
              id: 'financial_guarantee_amount',
              defaultMessage: 'Montant de la garantie financière'
            })}
            name="companyFinancialGuaranteeAmount"
          />
        </FormSection>
        <FormSection
          defaultOpened
          title={intl.formatMessage({
            id: 'contacts',
            defaultMessage: 'Contacts'
          })}
        >
          <PhoneField name="companyPhone" />
          <EmailField name="companyEmail" />
          <FormTextareaField
            label={intl.formatMessage({
              id: 'postal_address',
              defaultMessage: 'Adresse postale'
            })}
            name="companyPostalAddress"
          />
        </FormSection>
      </SimpleGrid>

      {editable
        ? <ButtonGroup>
          <FormResetButton />
          <FormSaveButton />
        </ButtonGroup>
        : null}
    </SmartForm>
  )
}

export default CompanySettingsForm
